import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { ICreateUnityUserBody, IAskIOTUserDetails } from '@/types';
import { apiInstance } from '@/utils';
import axios from 'axios';

/**
 * Retrieves user details from the Ask IoT API
 * @param accessToken - Optional access token for direct authentication
 * @returns User details or null if request fails
 */
export const getAskIotUserDetails = async (accessToken?: string): Promise<IAskIOTUserDetails | null> => {
  try {
    let resp: any = {};
    resp = accessToken
      ? await axios({
          method: 'GET',
          url: `${process.env.NEXT_PUBLIC_API_ENDPOINT}${API_ENDPOINTS.getAskIotUserDetails}`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
      : await apiInstance({
          method: 'GET',
          url: API_ENDPOINTS.getAskIotUserDetails,
        });

    return resp.data;
  } catch (_error) {
    return null;
  }
};

/**
 * Creates a new Ask IoT user
 * @returns New user details or null if creation fails
 */
export const createAskIotUser = async (): Promise<IAskIOTUserDetails | null> => {
  try {
    const resp = await apiInstance({
      method: 'POST',
      url: API_ENDPOINTS.createAskIotUser,
    });
    return resp.data;
  } catch (_error) {
    return null;
  }
};

export const createUnityUser = async (data: ICreateUnityUserBody): Promise<{ message: string }> => {
  const resp = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.createUnityUser,
  });

  return resp.data;
};

export const createStandaloneUser = async (
  data: ICreateUnityUserBody,
): Promise<{
  message: string;
  data: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
    };
    workspace: {
      id: number;
      name: string;
      slug: string;
      joinCode: string;
    };
    stripeCustomerId: string;
  };
}> => {
  const resp = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.createStandaloneUser,
  });

  return resp.data;
};

export const getCalendarTimeSlots = async (
  vendor_id: number,
  userId: string,
): Promise<{
  authId: number;
  profilePictureUrl: string;
  slots: {
    start: string;
    end: string;
  }[];
}> => {
  // Get the user's timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params: {
      vendor_id,
      userId,
      timezone, // Add the timezone parameter here
    },
    url: API_ENDPOINTS.getCalendarTimeSlots,
  });

  const { authId, profilePictureUrl, slots } = result.data;
  return { authId, profilePictureUrl, slots };
};

export const updateUserCalendarSettings = async (data: { slug: string }) => {
  const resp = await apiInstance({
    method: 'PUT',
    data,
    url: API_ENDPOINTS.updateUserCalendarSettings,
  });

  return resp.data;
};
